import { useState } from "react";
import "./EinrichtungStarklarIOS.scss";
import { eighteenYearsAgo, minDate, transformPayload } from "../../Constants";
import { sendToAzureFunction, getFormData, deleteToken } from "../../services/ApiService";
import { handleFormSubmit } from "../../services/FormService";
import storePhoto from "../../resources/storePhoto.png";

interface EinrichtungStarklarIOSProps {
  onSubmitCallback: (status: string) => void;
  tokenID: string;
  confirmedEmail: string;
  processID: number;
}

const EinrichtungStarklarIOS: React.FC<EinrichtungStarklarIOSProps> = ({ onSubmitCallback, tokenID, confirmedEmail, processID }) => {
  const [isExistingAccount, setIsExistingAccount] = useState(true);
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [securePin, setSecurePin] = useState("");
  const [confirmSecurePin, setConfirmSecurePin] = useState("");
  const [vornameError, setVornameError] = useState("");
  const [nachnameError, setNachnameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [securePinError, setSecurePinError] = useState("");
  const [confirmSecurePinError, setConfirmSecurePinError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");

  const handleCancel = async () => {
    try {
      await deleteToken(tokenID!);
      // After deletion, reload to trigger token validation and show error message
      window.location.reload();
    } catch (error) {
      // Optionally, show an error if deletion failed
      alert("Fehler beim Abbrechen des Vorgangs. Bitte versuchen Sie es erneut.");
    }
  };

  // Validate Vorname
  const validateVorname = (value: string) => {
    if (!value.trim()) {
      setVornameError("Dieses Feld darf nicht leer sein.");
      return false;
    } else {
      setVornameError("");
      return true;
    }
  };

  // Validate Nachname
  const validateNachname = (value: string) => {
    if (!value.trim()) {
      setNachnameError("Dieses Feld darf nicht leer sein.");
      return false;
    } else {
      setNachnameError("");
      return true;
    }
  };

  // Validate email
  const validateEmail = (value: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (!regex.test(value)) {
      setEmailError("Ungültige E-Mail-Adresse.");
      return false;
    } else {
      setEmailError("");
      return true;
    }
  };

  // Validate confirm email
  const validateConfirmEmail = (value: string) => {
    if (!value) {
      setConfirmEmailError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (value.toLowerCase() !== email.toLowerCase()) {
      setConfirmEmailError("E-Mail-Adressen stimmen nicht überein.");
      return false;
    } else {
      setConfirmEmailError("");
      return true;
    }
  };

  // Validate password
  const validatePassword = (value: string) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (!value) {
      setPasswordError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (!regex.test(value)) {
      setPasswordError(
        "Passwort muss mindestens 8 Zeichen lang sein, Groß- und Kleinbuchstaben, eine Zahl und ein Sonderzeichen enthalten."
      );
      return false;
    } else {
      setPasswordError("");
      return true;
    }
  };

  // Validate confirm password
  const validateConfirmPassword = (value: string) => {
    if (!value) {
      setConfirmPasswordError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (value !== password) {
      setConfirmPasswordError("Passwörter stimmen nicht überein.");
      return false;
    } else {
      setConfirmPasswordError("");
      return true;
    }
  };

  // Validate Secure PIN
  const validateSecurePin = (value: string) => {
    const pinRegex = /^\d{4}$/;
    if (!value.trim()) {
      setSecurePinError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (!pinRegex.test(value)) {
      setSecurePinError("Bitte geben Sie eine 4-stellige PIN ein.");
      return false;
    } else if (confirmSecurePin && value !== confirmSecurePin) {
      setConfirmSecurePinError("Die PINs stimmen nicht überein.");
      return false;
    } else {
      setSecurePinError("");
      return true;
    }
  };

  // Validate Confirm Secure PIN
  const validateConfirmSecurePin = (value: string) => {
    if (!value.trim()) {
      setConfirmSecurePinError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (value !== securePin) {
      setConfirmSecurePinError("Die PINs stimmen nicht überein.");
      return false;
    } else {
      setConfirmSecurePinError("");
      return true;
    }
  };

  // Validate phone number
  const validatePhoneNumber = (value: string) => {
    const regex = /^\+?[0-9\s\-]{9,20}$/;
    if (hasConsent && !value) {
      setPhoneNumberError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (hasConsent && !regex.test(value)) {
      setPhoneNumberError("Ungültige Telefonnummer.");
      return false;
    } else {
      setPhoneNumberError("");
      return true;
    }
  };

  // Validate the form based on the selected option
  const validateForm = () => {
    let isValid = true;

    if (isExistingAccount) {
      // Validate existing account fields
      isValid = validateEmail(email) && isValid;
      isValid = validateConfirmEmail(confirmEmail) && isValid;
      isValid = validatePassword(password) && isValid;
      isValid = validateConfirmPassword(confirmPassword) && isValid;
    } else {
      // Validate new account fields
      isValid = validateVorname(vorname) && isValid;
      isValid = validateNachname(nachname) && isValid;
      isValid = validateEmail(email) && isValid;
      isValid = validateConfirmEmail(confirmEmail) && isValid;
      isValid = validatePassword(password) && isValid;
      isValid = validateConfirmPassword(confirmPassword) && isValid;
    }

    // Validate Secure PIN for both forms
    isValid = validateSecurePin(securePin) && isValid;
    isValid = validateConfirmSecurePin(confirmSecurePin) && isValid;

    // Add phone validation if consent is given
    if (hasConsent) {
      isValid = validatePhoneNumber(phoneNumber) && isValid;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    handleFormSubmit(
      e,
      validateForm,
      getFormData,
      transformPayload,
      sendToAzureFunction,
      setIsSubmitted,
      onSubmitCallback,
      tokenID,
      confirmedEmail,
      processID
    );
  };

  return (
    <div className="email-form">
      <h2 style={{ marginTop: 0 }}>Einrichtung Startklar Premium und Komfort für iPhone</h2>
      <img
        src={storePhoto}
        alt="Store Photo"
        style={{ width: "100%", height: "auto", maxWidth: "100%", borderRadius: "10px", marginBottom: "20px" }}
      />

      <p style={{ width: "87%", border: "1px solid black", backgroundColor: "#f0f0f0", padding: "15px", borderRadius: "10px", textAlign: "center", marginBottom: "20px" }}>
        Bitte geben Sie Ihre Daten für die Durchführung des Services ein
      </p>

      <form onSubmit={handleSubmit}>
        {/* Switch */}
        <div className="form-group">
          <div className="switch-container">
            <span className={!isExistingAccount ? "active" : ""}>Neue Apple ID</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={isExistingAccount}
                onChange={() => setIsExistingAccount(!isExistingAccount)}
              />
              <span className="slider"></span>
            </label>
            <span className={isExistingAccount ? "active" : ""}>Vorhandene Apple ID</span>
          </div>
        </div>

        {isExistingAccount ? (
          <>
            {/* Existing Account Fields */}
            <div className="form-group">
              <label>E-Mail-Adresse</label>
              <input
                type="email"
                value={email}
                name="email"
                onChange={(e) => {
                  setEmail(e.target.value);
                  validateEmail(e.target.value);
                }}
                required
              />
              <small>Geben Sie die mit dem Konto verbundene E-Mail ein.</small>
              {isSubmitted && emailError && <small className="error-message">{emailError}</small>}
            </div>

            <div className="form-group">
              <label>E-Mail-Adresse bestätigen</label>
              <input
                type="email"
                value={confirmEmail}
                onChange={(e) => {
                  setConfirmEmail(e.target.value);
                  validateConfirmEmail(e.target.value);
                }}
                required
              />
              <small>Bitte geben Sie Ihre E-Mail erneut ein.</small>
              {isSubmitted && confirmEmailError && (
                <small className="error-message">{confirmEmailError}</small>
              )}
            </div>

            <div className="form-group">
              <label>Passwort</label>
              <input
                type="password"
                value={password}
                name="password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  validatePassword(e.target.value);
                }}
                required
              />
              <small>Bitte geben Sie Ihr Passwort ein.</small>
              {isSubmitted && passwordError && (
                <small className="error-message">{passwordError}</small>
              )}
            </div>

            <div className="form-group">
              <label>Passwort bestätigen</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  validateConfirmPassword(e.target.value);
                }}
                required
              />
              <small>Bitte geben Sie Ihr Passwort erneut ein.</small>
              {isSubmitted && confirmPasswordError && (
                <small className="error-message">{confirmPasswordError}</small>
              )}
            </div>

            <div className="form-group">
              <label>Verifizierungscode</label>
              <input type="text" name="verifizierungscode" required />
              <small>Code vom alten Handy oder per SMS erhalten.</small>
            </div>
          </>
        ) : (
          <>
            {/* New Account Fields */}
            <div className="form-group">
              <label>Vorname</label>
              <input
                type="text"
                value={vorname}
                name="vorname"
                onChange={(e) => {
                  setVorname(e.target.value);
                  validateVorname(e.target.value);
                }}
                required
              />
              <small>Erforderlich von Apple für eine Apple ID</small>
              {isSubmitted && vornameError && (
                <small className="error-message">{vornameError}</small>
              )}
            </div>

            <div className="form-group">
              <label>Nachname</label>
              <input
                type="text"
                value={nachname}
                name="nachname"
                onChange={(e) => {
                  setNachname(e.target.value);
                  validateNachname(e.target.value);
                }}
                required
              />
              <small>Erforderlich von Apple für eine Apple ID</small>
              {isSubmitted && nachnameError && (
                <small className="error-message">{nachnameError}</small>
              )}
            </div>

            <div className="form-group">
              <label>Geburtsdatum</label>
              <input
                type="date"
                min={minDate}
                max={eighteenYearsAgo} // Only allow dates at least 18 years ago
                name="geburtsdatum" required />
              <small>
                Apple erfragt diese Daten, hier geht es darum was dem Kunden angezeigt wird
                (Jugendschutz)
              </small>
            </div>

            {/* Email and Confirm Email */}
            <div className="form-group">
              <label>Neue E-Mail-Adresse</label>
              <input
                type="email"
                value={email}
                name="email"
                onChange={(e) => {
                  setEmail(e.target.value);
                  validateEmail(e.target.value);
                }}
                required
              />
              <small>Ihre neue E-Mail-Adresse.</small>
              {isSubmitted && emailError && <small className="error-message">{emailError}</small>}
            </div>

            <div className="form-group">
              <label>Neue E-Mail-Adresse bestätigen</label>
              <input
                type="email"
                value={confirmEmail}
                onChange={(e) => {
                  setConfirmEmail(e.target.value);
                  validateConfirmEmail(e.target.value);
                }}
                required
              />
              <small>Bitte geben Sie Ihre E-Mail erneut ein.</small>
              {isSubmitted && confirmEmailError && (
                <small className="error-message">{confirmEmailError}</small>
              )}
            </div>

            {/* Password and Confirm Password */}
            <div className="form-group">
              <label>Passwort</label>
              <input
                type="password"
                value={password}
                name="password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  validatePassword(e.target.value);
                }}
                required
              />
              <small>Notwendig für die Apple ID</small>
              {isSubmitted && passwordError && (
                <small className="error-message">{passwordError}</small>
              )}
            </div>

            <div className="form-group">
              <label>Passwort bestätigen</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  validateConfirmPassword(e.target.value);
                }}
                required
              />
              <small>Bitte geben Sie Ihr Passwort erneut ein.</small>
              {isSubmitted && confirmPasswordError && (
                <small className="error-message">{confirmPasswordError}</small>
              )}
            </div>

            <div className="form-group">
              <label>Sonstiges / Kundenwunsch</label>
              <textarea name="sonstiges" rows={3} required></textarea>
              <small>Zusätzliche Wünsche oder Anmerkungen.</small>
            </div>
          </>
        )}

        {/* Secure PIN Fields - Added for both form versions */}
        <div style={{ borderTop: "1px solid lightgrey", marginTop: "20px" }}></div>
        <br />

        <div className="form-group">
          <label>Neue PIN</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={securePin}
            name="SecurePIN"
            onChange={(e) => {
              setSecurePin(e.target.value);
              validateSecurePin(e.target.value);
              if (confirmSecurePin) validateConfirmSecurePin(confirmSecurePin);
            }}
            required
          />
          <small>Bitte legen Sie eine sicure 4-stellige PIN fest. Sie benötigen diese PIN für den Zugriff auf Ihre Falldetails und können von einem Mitarbeiter des Geschäfts zur Überprüfung abgefragt werden. Behandeln Sie die PIN vertraulich und geben Sie sie nicht an Dritte weiter.</small>
          {isSubmitted && securePinError && (
            <small className="error-message">{securePinError}</small>
          )}
        </div>

        <div className="form-group">
          <label>Neue PIN bestätigen</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={confirmSecurePin}
            onChange={(e) => {
              setConfirmSecurePin(e.target.value);
              validateConfirmSecurePin(e.target.value);
            }}
            required
          />
          <small>Bitte bestätigen Sie Ihre 4-stellige PIN.</small>
          {isSubmitted && confirmSecurePinError && (
            <small className="error-message">{confirmSecurePinError}</small>
          )}
        </div>

        <br />
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        {/* Consent Checkbox and Phone Number */}
        <div style={{ marginTop: "20px" }} className="form-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={hasConsent}
              onChange={(e) => {
                setHasConsent(e.target.checked);
                if (!e.target.checked) {
                  setPhoneNumber("");
                  setPhoneNumberError("");
                }
              }}
            />
            <span className="checkmark"></span>
            Ich stimme zu, dass ich für Angelegenheiten im Zusammenhang mit dieser Anfrage kontaktiert werden darf.
          </label>
        </div>

        {hasConsent && (
          <div className="form-group">
            <label>Telefonnummer</label>
            <input
              type="tel"
              value={phoneNumber}
              name="HandyNummer"
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                validatePhoneNumber(e.target.value);
              }}
              required={hasConsent}
            />
            <small>Bitte geben Sie eine gültige Telefonnummer ein.</small>
            {isSubmitted && phoneNumberError && (
              <small className="error-message">{phoneNumberError}</small>
            )}
          </div>
        )}

        {/* Submit Button */}
        <button type="submit">Absenden</button>
        <button type="button" onClick={handleCancel} style={{ backgroundColor: "red", marginBottom: 0 }}>
          Abbrechen
        </button>
        <small style={{ color: "#777" }}>Durch das Abbrechen wird der Vorgang gestoppt und Ihre Daten werden nicht weiter verwendet</small>
      </form>
    </div>
  );
};

export default EinrichtungStarklarIOS;